import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { DataService } from 'src/api/services';
import { SearchSource, SearchItem } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { CameraWatchViewItem } from '../../views/camera-watch/camera-watch.component';

interface SelectableCamera {
  siteId: string;
  siteName: string;
  siteUri: string;
  name: string;
  description: string;
  uri: string;
  siteType: string;
  selected: boolean;
}

@Component({
  selector: 'app-generic-camera-selection-modal',
  templateUrl: './generic-camera-selection-modal.component.html',
  styleUrls: ['./generic-camera-selection-modal.component.scss']
})
export class GenericCameraSelectionModalComponent implements OnInit {
  @Input() existingItems: CameraWatchViewItem[] = [];
  @Input() maxItems: number = 9;
  @Output() close = new EventEmitter<void>();
  @Output() itemsSelected = new EventEmitter<CameraWatchViewItem[]>();

  public cameras: SelectableCamera[] = [];
  public loadingCameras = true;
  public errorCameras = false;
  public searchTerm = '';

  constructor(
    public app: ApplicationConfigService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.loadCameras();
  }

  private loadCameras(): void {
    this.loadingCameras = true;
    this.errorCameras = false;

    // Search for time series with siteType containing "camera" (case-insensitive)
    // Using DataService.DataSearch to find media time series
    this.dataService.DataSearch({
      customerId: this.app.customerId,
      type: 'Media', // Search for media time series
      search: '', // Empty search to get all, will filter by siteType
      searchSize: 500,
      pageIndex: 1,
      includeAlarms: false,
      filterOld: true
    }).subscribe({
      next: (results: SearchSource[]) => {
        const cameras: SelectableCamera[] = [];
        
        // Process search results and filter by siteType containing "camera"
        results.forEach(source => {
          // Check if siteType contains "camera" (case-insensitive)
          if (source.siteType && source.siteType.toLowerCase().includes('camera')) {
            source.items?.forEach(item => {
              cameras.push({
                siteId: source.siteUri || '',
                siteName: source.siteName || 'Unknown Site',
                siteUri: source.siteUri || '',
                name: item.name || 'Unnamed Camera',
                description: item.description || '',
                uri: item.uri || '',
                siteType: source.siteType || '',
                selected: false
              });
            });
          }
        });

        this.cameras = cameras;
        this.loadingCameras = false;
      },
      error: (err) => {
        console.error('Error loading cameras:', err);
        this.errorCameras = true;
        this.loadingCameras = false;
      }
    });
  }

  public toggleCamera(selectableCamera: SelectableCamera): void {
    if (this.canSelectMore() || selectableCamera.selected) {
      selectableCamera.selected = !selectableCamera.selected;
    }
  }

  public onConfirm(): void {
    const selectedItems: CameraWatchViewItem[] = [];

    // Add selected cameras using URI as the unique identifier
    this.cameras
      .filter(c => c.selected)
      .forEach(c => {
        selectedItems.push({
          type: 'camera',
          id: c.uri, // Use URI as the unique identifier
          name: c.name,
          uri: c.uri
        });
      });

    this.itemsSelected.emit(selectedItems);
  }

  public onClose(): void {
    this.close.emit();
  }

  public get selectedCount(): number {
    return this.cameras.filter(c => c.selected).length;
  }

  public get availableSlots(): number {
    return this.maxItems - this.existingItems.length;
  }

  public canSelectMore(): boolean {
    return this.selectedCount < this.availableSlots;
  }

  public get filteredCameras(): SelectableCamera[] {
    if (!this.searchTerm) {
      return this.cameras;
    }
    const term = this.searchTerm.toLowerCase();
    return this.cameras.filter(c => 
      c.name?.toLowerCase().includes(term) ||
      c.siteName?.toLowerCase().includes(term) ||
      c.siteUri?.toLowerCase().includes(term) ||
      c.uri?.toLowerCase().includes(term) ||
      c.description?.toLowerCase().includes(term)
    );
  }

  public isCameraAlreadyAdded(camera: SelectableCamera): boolean {
    return this.existingItems.some(item => 
      item.type === 'camera' && item.uri === camera.uri
    );
  }
}
